import {
  Component,
  Inject,
  OnInit
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material';
import { environment } from '@app/../environments/environment';
import {
  PromoBannerPosition,
  PromoBannerReferenceType,
  PromoBannerSaveRequest,
  PromoBlogBannerFormData
} from '@app/model/promo-banner-management.model';
import {
  parsePromoBannerStyle,
  PROMO_BANNER_DEFAULT_STYLE,
  serializePromoBannerStyle
} from '@app/model/promo-banner-style.model';
import {
  PromoBannerManagementDataService
} from '@app/services/promo-banner-management-data.service';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import {
  finalize,
  switchMap
} from 'rxjs/operators';

@Component({
  selector: 'meu-promo-blog-banner-form',
  templateUrl: './promo-blog-banner-form.component.html',
  styleUrls: ['./promo-blog-banner-form.component.scss']
})
export class PromoBlogBannerFormComponent implements OnInit {
  isSaving = false;

  selectedImageFile: File | null = null;
  imagePreviewUrl = '';

  bannerForm = new FormGroup({
    title: new FormControl('', [
      Validators.required
    ]),
    slug: new FormControl('', [
      Validators.required
    ]),
    description: new FormControl('', [
      Validators.required
    ]),
    buttonLabel: new FormControl('Xem blog', [
      Validators.required
    ]),
    textColor: new FormControl(
      PROMO_BANNER_DEFAULT_STYLE.textColor
    ),
    buttonBg: new FormControl(
      PROMO_BANNER_DEFAULT_STYLE.buttonBg
    ),
    buttonText: new FormControl(
      PROMO_BANNER_DEFAULT_STYLE.buttonText
    ),
    showTitle: new FormControl(true),
    showDescription: new FormControl(true),
    showButton: new FormControl(true),
    isActive: new FormControl(true)
  });

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: PromoBlogBannerFormData,
    private dialogRef:
      MatDialogRef<PromoBlogBannerFormComponent>,
    private promoBannerService:
      PromoBannerManagementDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (!this.data.banner) {
      return;
    }

    const style = parsePromoBannerStyle(
      this.data.banner.add_info,
      'Xem blog'
    );

    this.bannerForm.patchValue({
      title: this.data.banner.title,
      slug: this.data.banner.slug,
      description: this.data.banner.description,
      buttonLabel: style.label,
      textColor: style.textColor,
      buttonBg: style.buttonBg,
      buttonText: style.buttonText,
      showTitle: style.showTitle,
      showDescription: style.showDescription,
      showButton: style.showButton,
      isActive: this.data.banner.is_active
    });

    this.imagePreviewUrl =
      this.getImageUrl(this.data.banner.image);
  }

  private getMissingFieldLabels(): string[] {
    const labels = {
      title: 'Tiêu đề',
      slug: 'Slug',
      description: 'Mô tả',
      buttonLabel: 'Nội dung nút'
    };

    return Object.keys(labels)
      .filter((key) => {
        const control = this.bannerForm.get(key);

        return !!control && control.invalid;
      })
      .map((key) => labels[key]);
  }

  resetColors(): void {
    this.bannerForm.patchValue({
      textColor:
        PROMO_BANNER_DEFAULT_STYLE.textColor,
      buttonBg: PROMO_BANNER_DEFAULT_STYLE.buttonBg,
      buttonText:
        PROMO_BANNER_DEFAULT_STYLE.buttonText
    });
  }

  get modalTitle(): string {
    return this.data.banner
      ? 'Chỉnh sửa Banner Blog'
      : 'Thiết lập Banner Blog';
  }

  onSelectedImage(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (
      !input.files ||
      input.files.length === 0
    ) {
      return;
    }

    const file = input.files[0];

    if (!file.type.startsWith('image/')) {
      input.value = '';
      this.toastr.error(
        'Vui lòng chọn một file hình ảnh'
      );
      return;
    }

    this.selectedImageFile = file;

    const reader = new FileReader();

    reader.onload = () => {
      this.imagePreviewUrl = reader.result as string;
    };

    reader.readAsDataURL(file);
  }

  closeModal(): void {
    if (this.isSaving) {
      return;
    }

    this.dialogRef.close();
  }

  saveBanner(): void {
    this.bannerForm.markAllAsTouched();

    if (this.bannerForm.invalid) {
      this.toastr.error(
        'Vui lòng nhập: ' +
          this.getMissingFieldLabels().join(', ')
      );
      return;
    }

    if (
      !this.data.banner &&
      !this.selectedImageFile
    ) {
      this.toastr.error(
        'Vui lòng chọn hình ảnh banner'
      );
      return;
    }

    const formValue = this.bannerForm.value;

    const request: PromoBannerSaveRequest = {
      slug: formValue.slug.trim(),
      link: '/blog',
      position: PromoBannerPosition.Blog,
      sort_order: 1,
      add_info: serializePromoBannerStyle({
        label: formValue.buttonLabel,
        textColor: formValue.textColor,
        buttonBg: formValue.buttonBg,
        buttonText: formValue.buttonText,
        showTitle: formValue.showTitle !== false,
        showDescription: formValue.showDescription !== false,
        showButton: formValue.showButton !== false
      }),
      banner_group_id: this.data.groupId,
      reference_type:
        PromoBannerReferenceType.General,
      reference_id: null,
      title: formValue.title.trim(),
      description: formValue.description.trim(),
      is_active: formValue.isActive === true,
      start_date: null,
      end_date: null
    };

    const saveRequest = (this.data.banner
      ? this.promoBannerService.updateBanner(
          this.data.banner.id,
          request
        )
      : this.promoBannerService.createBanner(request)
    ).pipe(
      switchMap((savedBanner) => {
        if (!this.selectedImageFile) {
          return of(savedBanner);
        }

        return this.promoBannerService
          .uploadBannerImage(
            savedBanner.id,
            this.selectedImageFile
          );
      })
    );

    this.isSaving = true;

    saveRequest
      .pipe(
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(
        () => {
          const message = this.data.banner
            ? 'Cập nhật banner thành công'
            : 'Tạo banner thành công';

          this.toastr.success(message);

          this.dialogRef.close({
            saved: true
          });
        },
        (error) => {
          console.error(
            'Cannot save promo blog banner',
            error
          );

          const message =
            error &&
            error.error &&
            error.error.message
              ? error.error.message
              : 'Không thể lưu banner';

          this.toastr.error(message);
        }
      );
  }

  private getImageUrl(image: string): string {
    if (!image) {
      return '';
    }

    if (
      image.startsWith('http://') ||
      image.startsWith('https://') ||
      image.startsWith('data:')
    ) {
      return image;
    }

    return (
      environment.backendhost.replace(/\/$/, '') +
      '/' +
      image.replace(/^\//, '')
    );
  }
}
